// LanguageManagementModal.jsx
import React, { useState } from 'react';
import CloseIcon from '@mui/icons-material/Close';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import LanguageIcon from '@mui/icons-material/Language';

const LanguageManagementModal = ({ 
  isOpen, 
  onClose, 
  languages = [], 
  onAddLanguage, 
  onDeleteLanguage,
  availableLanguages = []
}) => {
  const [selectedLanguage, setSelectedLanguage] = useState('');
  const [isAdding, setIsAdding] = useState(false);
  const [deletingLanguageId, setDeletingLanguageId] = useState(null);

  const DEFAULT_LANGUAGE_IDS = [999001, 999002, 999003, 999004];
  const MAX_TOTAL_LANGUAGES = 6;

  if (!isOpen) return null;

  const handleAdd = async () => {
    if (!selectedLanguage) return;
    
    setIsAdding(true);
    try {
      await onAddLanguage(selectedLanguage);
      setSelectedLanguage('');
    } catch (error) {
      console.error('Error adding language:', error);
    } finally {
      setIsAdding(false);
    }
  };

  const handleDelete = async (languageId) => {
    setDeletingLanguageId(languageId);
    try {
      await onDeleteLanguage(languageId);
    } catch (error) {
      console.error('Error deleting language:', error);
    } finally {
      setDeletingLanguageId(null);
    }
  };

  const isDefaultLanguage = (languageId) => {
    return DEFAULT_LANGUAGE_IDS.includes(languageId);
  };

  const canAddMore = languages.length < MAX_TOTAL_LANGUAGES;

  const styles = {
    overlay: {
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 9999,
      padding: '16px'
    },
    container: {
      backgroundColor: 'white',
      borderRadius: '8px',
      boxShadow: '0 10px 25px rgba(0, 0, 0, 0.3)',
      width: '100%',
      maxWidth: '500px',
      maxHeight: '90vh',
      display: 'flex',
      flexDirection: 'column'
    },
    header: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      padding: '24px',
      borderBottom: '1px solid #e5e7eb'
    },
    titleWrapper: {
      display: 'flex',
      alignItems: 'center',
      gap: '8px'
    },
    title: {
      fontSize: '20px',
      fontWeight: 600,
      color: '#111827',
      margin: 0
    },
    closeBtn: {
      background: 'none',
      border: 'none',
      color: '#9ca3af',
      cursor: 'pointer',
      padding: '4px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      transition: 'color 0.2s'
    },
    content: {
      flex: 1,
      overflowY: 'auto',
      padding: '24px'
    },
    addSection: {
      marginBottom: '24px'
    },
    label: {
      display: 'block',
      fontSize: '14px',
      fontWeight: 500,
      color: '#374151',
      marginBottom: '8px'
    },
    addControls: {
      display: 'flex',
      gap: '8px'
    },
    select: {
      flex: 1,
      padding: '8px 12px',
      border: '1px solid #d1d5db',
      borderRadius: '6px',
      fontSize: '14px',
      outline: 'none',
      transition: 'border-color 0.2s, box-shadow 0.2s'
    },
    selectDisabled: {
      backgroundColor: '#f3f4f6',
      cursor: 'not-allowed'
    },
    addBtn: {
      padding: '8px 16px',
      backgroundColor: '#2563eb',
      color: 'white',
      border: 'none',
      borderRadius: '6px',
      fontSize: '14px',
      fontWeight: 500,
      cursor: 'pointer',
      display: 'flex',
      alignItems: 'center',
      gap: '6px',
      transition: 'background-color 0.2s',
      whiteSpace: 'nowrap'
    },
    addBtnDisabled: {
      backgroundColor: '#d1d5db',
      cursor: 'not-allowed'
    },
    listSection: {
      marginTop: '24px'
    },
    emptyState: {
      textAlign: 'center',
      padding: '32px',
      color: '#6b7280',
      fontSize: '14px'
    },
    list: {
      display: 'flex',
      flexDirection: 'column',
      gap: '8px'
    },
    listItem: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      padding: '12px',
      backgroundColor: '#f9fafb',
      border: '1px solid #e5e7eb',
      borderRadius: '6px',
      transition: 'background-color 0.2s'
    },
    languageInfo: {
      display: 'flex',
      alignItems: 'center',
      gap: '8px'
    },
    languageName: {
      fontWeight: 500,
      color: '#111827',
      fontSize: '14px'
    },
    defaultBadge: {
      backgroundColor: '#dbeafe',
      color: '#1e40af',
      padding: '2px 8px',
      borderRadius: '4px',
      fontSize: '11px',
      fontWeight: 600
    },
    deleteBtn: {
      background: 'none',
      border: 'none',
      color: '#dc2626',
      cursor: 'pointer',
      padding: '8px',
      borderRadius: '4px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      transition: 'background-color 0.2s, color 0.2s'
    },
    deleteBtnDisabled: {
      opacity: 0.5,
      cursor: 'not-allowed'
    },
    deletingText: {
      fontSize: '12px'
    },
    footer: {
      padding: '24px',
      borderTop: '1px solid #e5e7eb'
    },
    closeFooterBtn: {
      width: '100%',
      padding: '10px 16px',
      backgroundColor: '#e5e7eb',
      color: '#1f2937',
      border: 'none',
      borderRadius: '6px',
      fontSize: '14px',
      fontWeight: 500,
      cursor: 'pointer',
      transition: 'background-color 0.2s'
    },
    infoText: {
      fontSize: '12px',
      color: '#6b7280',
      marginTop: '4px'
    }
  };

  return (
    <div style={styles.overlay}>
      <div style={styles.container}>
        <div style={styles.header}>
          <div style={styles.titleWrapper}>
            <LanguageIcon style={{ width: '20px', height: '20px', color: '#1976d2' }} />
            <h2 style={styles.title}>Manage Languages</h2>
          </div>
          <button
            onClick={onClose}
            style={styles.closeBtn}
            onMouseEnter={(e) => e.currentTarget.style.color = '#4b5563'}
            onMouseLeave={(e) => e.currentTarget.style.color = '#9ca3af'}
          >
            <CloseIcon style={{ width: '20px', height: '20px' }} />
          </button>
        </div>

        <div style={styles.content}>
          <div style={styles.addSection}>
            <label style={styles.label}>
              Add New Language
            </label>
            <div style={styles.addControls}>
              <select
                value={selectedLanguage}
                onChange={(e) => setSelectedLanguage(e.target.value)}
                disabled={isAdding || availableLanguages.length === 0 || !canAddMore}
                style={{
                  ...styles.select,
                  ...(isAdding || availableLanguages.length === 0 || !canAddMore ? styles.selectDisabled : {})
                }}
                onFocus={(e) => {
                  if (!e.currentTarget.disabled) {
                    e.currentTarget.style.borderColor = '#2563eb';
                    e.currentTarget.style.boxShadow = '0 0 0 3px rgba(37, 99, 235, 0.1)';
                  }
                }}
                onBlur={(e) => {
                  e.currentTarget.style.borderColor = '#d1d5db';
                  e.currentTarget.style.boxShadow = 'none';
                }}
              >
                <option value="">
                  {!canAddMore 
                    ? 'Maximum languages reached' 
                    : availableLanguages.length === 0 
                      ? 'No more languages available' 
                      : 'Select a language'}
                </option>
                {availableLanguages.map((lang) => (
                  <option key={lang.id} value={lang.id}>
                    {lang.name}
                  </option>
                ))}
              </select>
              <button
                onClick={handleAdd}
                disabled={!selectedLanguage || isAdding || !canAddMore}
                style={{
                  ...styles.addBtn,
                  ...(!selectedLanguage || isAdding || !canAddMore ? styles.addBtnDisabled : {})
                }}
                onMouseEnter={(e) => {
                  if (!e.currentTarget.disabled) {
                    e.currentTarget.style.backgroundColor = '#1d4ed8';
                  }
                }}
                onMouseLeave={(e) => {
                  if (!e.currentTarget.disabled) {
                    e.currentTarget.style.backgroundColor = '#2563eb';
                  }
                }}
              >
                <AddIcon style={{ width: '16px', height: '16px' }} />
                {isAdding ? 'Adding...' : 'Add'}
              </button>
            </div>
            <p style={styles.infoText}>
              {languages.length}/{MAX_TOTAL_LANGUAGES} languages selected
            </p>
          </div>

          <div style={styles.listSection}>
            <label style={styles.label}>
              Current Languages ({languages.length})
            </label>
            {languages.length === 0 ? (
              <div style={styles.emptyState}>
                No languages added yet
              </div>
            ) : (
              <div style={styles.list}>
                {languages.map((lang) => {
                  const isDefault = isDefaultLanguage(lang.id);
                  return (
                    <div
                      key={lang.id}
                      style={styles.listItem}
                      onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f3f4f6'}
                      onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#f9fafb'}
                    >
                      <div style={styles.languageInfo}>
                        <span style={styles.languageName}>{lang.name}</span>
                        {isDefault && (
                          <span style={styles.defaultBadge}>DEFAULT</span>
                        )}
                      </div>
                      {!isDefault && (
                        <button
                          onClick={() => handleDelete(lang.id)}
                          disabled={deletingLanguageId === lang.id}
                          style={{
                            ...styles.deleteBtn,
                            ...(deletingLanguageId === lang.id ? styles.deleteBtnDisabled : {})
                          }}
                          title="Delete language"
                          onMouseEnter={(e) => {
                            if (!e.currentTarget.disabled) {
                              e.currentTarget.style.backgroundColor = '#fee2e2';
                              e.currentTarget.style.color = '#b91c1c';
                            }
                          }}
                          onMouseLeave={(e) => {
                            if (!e.currentTarget.disabled) {
                              e.currentTarget.style.backgroundColor = 'transparent';
                              e.currentTarget.style.color = '#dc2626';
                            }
                          }}
                        >
                          {deletingLanguageId === lang.id ? (
                            <span style={styles.deletingText}>Deleting...</span>
                          ) : (
                            <DeleteIcon style={{ width: '16px', height: '16px' }} />
                          )}
                        </button>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        <div style={styles.footer}>
          <button
            onClick={onClose}
            style={styles.closeFooterBtn}
            onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#d1d5db'}
            onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#e5e7eb'}
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default LanguageManagementModal;